import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { Helmet } from 'react-helmet';
import { window } from '../../globals';
import { useAsyncStatus, useOnMount } from '../../hooks';
import * as api from '../../redux/api';
import ChangeOrgComponent from './ChangeOrgCard';
import ChangeSubcriptionComponent from './ChangeSubscriptionCard';
import ContributorsCard from './ContributorsCard';
import LicenseCorrectionCard from './LicenseCorrectionCard/LicenseCorrectionCard';
import ContributorCountComponent from './CustomContribCountCard';
import CustomProvidedBuildsQueueCard from './CustomProvidedBuildsQueueCard';
import FeatureControlCard from './FeatureControlCard';
import GithubOrgComponent from './GithubOrgComponent';
import OrgInfoCard from './OrgInfoCard';
import UserInfoComponent from './UserInfoCard';
import ReanalyzeQuickImportCard from './ReanalyzeQuickImportCard';
import type { Organization } from '../../types';
import type { FailedFetchResponse } from '../../helpers/errors';
import { FullOrganizationPackageCacheRebuildCard } from './FullOrganizationPackageCacheRebuildCard';
import BackfillLicenseIdentificationDataCard from './BackfillLicenseIdentificationDataCard';
import { BINARY_DECOMPOSITION, GLOBAL_LICENSE_CORRECTIONS } from '../../../shared/featureFlags/flagNames';
import { BackfillIssueSanitizedLocators } from './BackfillIssueSanitizedLocators';
import { FeatureFlagRequired } from '../generic';
import BillingTermCard from './BillingTermCard';
import { PREMIUM_PER_REVISION_PLAN_NAME } from '../../../shared/subscriptions';
import IssueCountBackfillCard from './IssueCountBackfillCard';
import ToggleInternalOrgCard from './ToggleInternalOrgCard/ToggleInternalOrgCard';
import CallToAction from '../CallToAction';
import PageHeadingBreadcrumbs from '../generic/PageHeadingBreadcrumbs';
import DeleteAllProjectsCard from './DeleteAllProjectsCard';
import Flex from '../generic/Flex';
import BackfillSBOMSummaryData from './BackfillSBOMSummaryData';
import { useIsFeatureEnabled } from '../../helpers/features';

const AdminDashboard: VoidFunctionComponent = () => {
  const [org, setOrg] = useState<Organization>(window.user?.organization as Organization);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const { status, onError, onPending, onSuccess } = useAsyncStatus();

  const hasBinaryDecomp = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  useOnMount(async () => {
    onPending();
    try {
      const res = await api.get<Organization>(`/api/organizations/${window.user?.organizationId as number}`);
      if (res.data) {
        setOrg({ ...org, ...res.data });
        onSuccess();
      }
    } catch (error) {
      onError(error as FailedFetchResponse);
    }
  });

  return (
    <>
      <CallToAction>
        <PageHeadingBreadcrumbs
          truncateAt={80}
          crumbs={[
            {
              to: '/admin',
              text: 'Admin Tools',
            },
            {
              text: window.user?.organization?.title ?? 'Organization',
            },
          ]}
        />
      </CallToAction>
      <div className="container margin-top admin-dashboard">
        <Helmet title="Admin Dashboard" />
        {generalError ? <div className="alert alert-error">{generalError}</div> : null}
        <div className="alert">
          Stuff on this page can have catastrophic effects if used incorrectly. Make sure you know what
          you&#39;re doing.
        </div>
        <div className="content">
          <div className="content--main">
            <ChangeOrgComponent />
            <ChangeSubcriptionComponent org={org} />
            {(org.stripe_plan === PREMIUM_PER_REVISION_PLAN_NAME || hasBinaryDecomp) &&
              <BillingTermCard />}
            <ContributorCountComponent org={org} setOrg={setOrg} isOrgLoading={status.isLoading} />
            <FeatureControlCard setError={setGeneralError} />
            <GithubOrgComponent />
            <FeatureFlagRequired flagName={GLOBAL_LICENSE_CORRECTIONS}>
              <LicenseCorrectionCard />
            </FeatureFlagRequired>
            {!window.on_prem && <CustomProvidedBuildsQueueCard />}
            <ReanalyzeQuickImportCard />
            <FullOrganizationPackageCacheRebuildCard org={org} />
            <BackfillLicenseIdentificationDataCard />
            <BackfillSBOMSummaryData />
            <BackfillIssueSanitizedLocators />
            {/* These cards use newer generics and styles, so we need to space them out with a flex. */}
            <Flex direction="column" grow={1} align="flex-start" gap={25}>
              <ToggleInternalOrgCard />
              <IssueCountBackfillCard />
              <DeleteAllProjectsCard />
            </Flex>
          </div>
          <div className="content--side">
            <UserInfoComponent />
            <OrgInfoCard />
            <ContributorsCard />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
